import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Calendar, HardDrive, Hash, ShieldOff } from 'lucide-react'
import { getScanStatus } from '../lib/api'
import type { ScanResult, ThreatCategory } from '../types/scan'
import ThreatMeter from '../components/ThreatMeter'
import FindingsTable from '../components/FindingsTable'
import DecompiledCodeViewer from '../components/DecompiledCodeViewer'
import styles from './ResultsPage.module.css'

const CATEGORY_ICONS: Record<ThreatCategory, string> = {
  backdoor:                '🚪',
  cryptominer:             '⛏️',
  obfuscation:             '🔀',
  exfiltration:            '📤',
  rat:                     '🐀',
  spyware:                 '👁️',
  ransomware:              '🔒',
  dropper:                 '💧',
  suspicious_network:      '🌐',
  suspicious_reflection:   '🪞',
  suspicious_classloading: '📦',
  unknown:                 '❓',
}

const CATEGORY_COLORS: Record<ThreatCategory, string> = {
  backdoor:                '#ef4444',
  cryptominer:             '#f59e0b',
  obfuscation:             '#8b5cf6',
  exfiltration:            '#ec4899',
  rat:                     '#dc2626',
  spyware:                 '#f97316',
  ransomware:              '#b91c1c',
  dropper:                 '#e879f9',
  suspicious_network:      '#3b82f6',
  suspicious_reflection:   '#06b6d4',
  suspicious_classloading: '#10b981',
  unknown:                 '#94a3b8',
}

const CATEGORY_LABELS: Record<ThreatCategory, string> = {
  backdoor:                'Backdoor',
  cryptominer:             'Kryptominer',
  obfuscation:             'Obfuskacja',
  exfiltration:            'Eksfiltracja danych',
  rat:                     'RAT (Trojan zdalny)',
  spyware:                 'Spyware',
  ransomware:              'Ransomware',
  dropper:                 'Dropper',
  suspicious_network:      'Podejrzana sieć',
  suspicious_reflection:   'Podejrzana refleksja',
  suspicious_classloading: 'Niebezpieczny ClassLoader',
  unknown:                 'Nieznane',
}

type Tab = 'overview' | 'findings' | 'code'

export default function ResultsPage() {
  const { scanId } = useParams<{ scanId: string }>()
  const [result, setResult] = useState<ScanResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('overview')

  useEffect(() => {
    if (!scanId) return
    getScanStatus(scanId)
      .then((status) => {
        if (status.result) setResult(status.result as ScanResult)
        else setError('Wyniki nie są jeszcze dostępne')
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Błąd ładowania wyników')
      })
  }, [scanId])

  if (error) {
    return (
      <div className={styles.error}>
        <ShieldOff size={40} />
        <p>{error}</p>
        <Link to="/" className={styles.backLink}>
          <ArrowLeft size={16} /> Powrót do skanera
        </Link>
      </div>
    )
  }

  if (!result) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <span>Ładowanie wyników...</span>
      </div>
    )
  }

  const { consensus } = result
  const formattedDate = result.completedAt
    ? new Date(result.completedAt).toLocaleString('pl-PL')
    : '—'
  const fileSizeFmt = result.fileSize
    ? result.fileSize < 1024 * 1024
      ? `${(result.fileSize / 1024).toFixed(1)} KB`
      : `${(result.fileSize / (1024 * 1024)).toFixed(1)} MB`
    : '—'

  const tabs: Array<{ id: Tab; label: string; count?: number }> = [
    { id: 'overview', label: 'Przegląd' },
    { id: 'findings', label: 'Znaleziska', count: consensus?.findings.length },
    { id: 'code',     label: 'Kod źródłowy', count: result.decompiledClasses?.length },
  ]

  return (
    <div className={styles.root}>
      {/* ── Pasek górny ─────────────────────────────────────────────────── */}
      <div className={styles.topBar}>
        <Link to="/" className={styles.backBtn}>
          <ArrowLeft size={16} />
          Nowe skanowanie
        </Link>
        <div className={styles.topBarActions} />
      </div>

      {/* ── Informacje o pliku ───────────────────────────────────────────── */}
      <div className={styles.fileInfo}>
        <div className={styles.fileInfoItem}>
          <HardDrive size={14} className={styles.fileInfoIcon} />
          <span className={styles.fileInfoLabel}>Plik:</span>
          <span className={styles.fileInfoValue}>{result.fileName}</span>
        </div>
        <div className={styles.fileInfoItem}>
          <HardDrive size={14} className={styles.fileInfoIcon} />
          <span className={styles.fileInfoLabel}>Rozmiar:</span>
          <span className={styles.fileInfoValue}>{fileSizeFmt}</span>
        </div>
        <div className={styles.fileInfoItem}>
          <Hash size={14} className={styles.fileInfoIcon} />
          <span className={styles.fileInfoLabel}>SHA-256:</span>
          <span className={`${styles.fileInfoValue} ${styles.mono}`}>
            {result.fileSha256?.substring(0, 16)}...
          </span>
        </div>
        <div className={styles.fileInfoItem}>
          <Calendar size={14} className={styles.fileInfoIcon} />
          <span className={styles.fileInfoLabel}>Skanowano:</span>
          <span className={styles.fileInfoValue}>{formattedDate}</span>
        </div>
      </div>

      {/* ── Zakładki ────────────────────────────────────────────────────── */}
      <div className={styles.tabs} role="tablist">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={styles.tabCount}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Zawartość zakładek ───────────────────────────────────────────── */}
      <div className={styles.tabContent}>

        {/* ── Zakładka: Przegląd ────────────────────────────────────────── */}
        {activeTab === 'overview' && (
          <div className={styles.overviewGrid} role="tabpanel">
            {consensus && (
              <div className={styles.meterSection}>
                <ThreatMeter
                  score={consensus.threatScore}
                  level={consensus.threatLevel}
                  confidence={consensus.confidence}
                />
                <div className={styles.meterMeta}>
                  <p className={styles.meterSummary}>{consensus.summary}</p>
                  <div className={styles.meterStats}>
                    <div className={styles.meterStat}>
                      <span className={styles.meterStatValue}>{consensus.modelsAgreed}/2</span>
                      <span className={styles.meterStatLabel}>Modele ukończone</span>
                    </div>
                    <div className={styles.meterStat}>
                      <span className={styles.meterStatValue}>{result.decompiledClasses?.length ?? 0}</span>
                      <span className={styles.meterStatLabel}>Klas przeanalizowanych</span>
                    </div>
                    <div className={styles.meterStat}>
                      <span className={styles.meterStatValue}>{consensus.findings.length}</span>
                      <span className={styles.meterStatLabel}>Znalezisk łącznie</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {consensus && consensus.categories.length > 0 && (
              <div className={styles.categoriesSection}>
                <h2 className={styles.sectionTitle}>Wykryte kategorie zagrożeń</h2>
                <div className={styles.categoryGrid}>
                  {consensus.categories.map((cat) => (
                    <div
                      key={cat}
                      className={styles.categoryCard}
                      style={{ borderColor: `${CATEGORY_COLORS[cat]}40` }}
                    >
                      <span className={styles.categoryEmoji}>{CATEGORY_ICONS[cat]}</span>
                      <span className={styles.categoryName} style={{ color: CATEGORY_COLORS[cat] }}>
                        {CATEGORY_LABELS[cat]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {consensus && consensus.findings.length > 0 && (
              <div className={styles.quickFindings}>
                <div className={styles.quickFindingsHeader}>
                  <h2 className={styles.sectionTitle}>Najważniejsze znaleziska</h2>
                  <button className={styles.viewAllBtn} onClick={() => setActiveTab('findings')}>
                    Pokaż wszystkie {consensus.findings.length} →
                  </button>
                </div>
                <div className={styles.quickFindingsList}>
                  {consensus.findings.slice(0, 5).map((f, i) => (
                    <div key={i} className={styles.quickFinding}>
                      <span
                        className={styles.quickFindingSeverity}
                        style={{ color: f.severity === 'critical' || f.severity === 'high' ? '#ef4444' : '#f59e0b' }}
                      >
                        {f.severity}
                      </span>
                      <code className={styles.quickFindingClass}>{f.className.split('.').pop()}</code>
                      <span className={styles.quickFindingDesc}>{f.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Zakładka: Znaleziska ──────────────────────────────────────── */}
        {activeTab === 'findings' && (
          <div role="tabpanel">
            <FindingsTable findings={consensus?.findings ?? []} />
          </div>
        )}

        {/* ── Zakładka: Kod źródłowy ────────────────────────────────────── */}
        {activeTab === 'code' && (
          <div role="tabpanel">
            <DecompiledCodeViewer classes={result.decompiledClasses ?? []} />
          </div>
        )}
      </div>
    </div>
  )
}
